import { createContext, useEffect, useState } from 'react';
import { AUTH_TOKEN, API_URL } from '../configs/AppConfig';
import fetch from '../auth/FetchInterceptor';
import {
  DIR_LTR,
  NAV_TYPE_TOP,
  THEME_TYPE_LIGHT,
} from '../constants/ThemeConstants';
import { MENU_ITEMS, PERMISSION_METRICS } from '../redux/constants/Auth';
import { requestMasterDataList } from '../services/HR/masterData';
import { EMPTY_MASTER_DATA, toOptions } from '../constants/MasterDataConstants';

/**
 * @typedef {Object} RootContextType
 * @property {string|null} token - Authentication token.
 * @property {string|null} p_m__ - Authentication p_m__.
 * @property {string|null} sideMenu - Side menu object
 * @property {string} themeColor - Theme color.
 * @property {string} theme - Theme type (light or dark).
 * @property {boolean} sideNavCollapsed - State of side navigation collapse.
 * @property {string} navType - Navigation type (side or top).
 * @property {string} direction - Text direction (LTR or RTL).
 * @property {boolean} mobileNav - State of mobile navigation collapse.
 * @property {Object} masterData - Client-maintained dropdown lists, keyed by list name.
 * @property {Function} masterOptions - (key) => JetaSelect options for that list.
 * @property {Function} gradeForTitle - (title) => { career_level, band } for a position title.
 * @property {Function} reloadMasterData - Refetches the lists after an admin edit.
 * @property {Function} changeToken - Function to change the authentication token.
 * @property {Function} changePermission - Function to change the authentication permission.
 * @property {Function} changeSideNavState - Function to toggle the state of side navigation collapse.
 * @property {Function} changeTheme - Function to change the theme type.
 * @property {Function} changeNavType - Function to change the navigation type.
 * @property {Function} changeDirection - Function to change the text direction.
 * @property {Function} changeMobileNavState - Function to toggle the state of mobile navigation collapse.
 */

/**
 * The context that provides the state and actions for the root components of the application.
 * @type {React.Context<RootContextType>}
 */
export const RootContext = createContext({
  token: null,
  userDetails: null,
  p_m__: null,
  sideMenu: null,
  themeColor: '',
  theme: THEME_TYPE_LIGHT,
  sideNavCollapsed: false,
  navType: NAV_TYPE_TOP,
  direction: DIR_LTR,
  mobileNav: false,
  userList: [],
  custProgramList: [],
  masterData: EMPTY_MASTER_DATA,
  masterOptions: () => [],
  gradeForTitle: () => ({ career_level: null, band: null }),
  reloadMasterData: () => {},
  changeToken: () => {},
  changeUserDetails: () => {},
  changePermission: () => {},
  changeSideMenu: () => {},
  changeSideNavState: () => {},
  changeTheme: () => {},
  changeNavType: () => {},
  changeDirection: () => {},
  changeMobileNavState: () => {},
  changeUserList: () => {},
  changeCustProgramList: () => {},
});

/**
 * Provides the RootContext to its children.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 * @returns {JSX.Element} The provider component that wraps the children with the RootContext.
 */
export default function RootContextProvider({ children }) {
  const [value, setValue] = useState(localStorage.getItem(AUTH_TOKEN) || null);
  const [user, setUser] = useState();
  const [permission, setPermission] = useState(
    localStorage.getItem(PERMISSION_METRICS) || null
  );
  const [sideMenu, setSideMenu] = useState(
    JSON.parse(localStorage.getItem(MENU_ITEMS)) || []
  );

  const [sideNavCollapsed, setSideNavCollapsed] = useState(false);
  const [navType, setNavType] = useState(
    localStorage.getItem('navtype') || NAV_TYPE_TOP
  );
  const [theme, setTheme] = useState(
    localStorage.getItem('theme') || THEME_TYPE_LIGHT
  );
  const [direction, setDirection] = useState(DIR_LTR);
  const [mobileNavCollapsed, setMobileNavCollapsed] = useState(false);
  const [userList, setUserList] = useState([]);
  const [custProgramList, setCustProgramList] = useState([]);
  const [masterData, setMasterData] = useState(EMPTY_MASTER_DATA);

  // The dropdown lists are small, change rarely and are needed by most HR
  // screens, so they are fetched once per session rather than per screen.
  function reloadMasterData() {
    return requestMasterDataList()
      .then(res => { if (res?.data) setMasterData({ ...EMPTY_MASTER_DATA, ...res.data }); })
      .catch(() => {});
  }

  useEffect(() => {
    if (!value) {
      setMasterData(EMPTY_MASTER_DATA);
      return;
    }
    reloadMasterData();
  }, [value]);

  function masterOptions(key) {
    return toOptions(masterData[key]);
  }

  // Career level and band are a property of the title, not a separate choice.
  function gradeForTitle(title) {
    const row = (masterData.position_title || []).find(r => r.value === title);
    return {
      career_level: row?.career_level ?? null,
      band: row?.band ?? null,
    };
  }

  useEffect(() => {
    if (!value) {
      setUser(undefined);
      return;
    }
    const userId = localStorage.getItem('user');
    if (!userId) return;
    fetch.get(`${API_URL}/api/app/subscriber-user/getById/${userId}`)
      .then(res => { if (res?.data) setUser(res.data); })
      .catch(() => {});
  }, [value]);

  const ctx = {
    token: value,
    userDetails: user,
    p_m__: permission,
    sideMenu: sideMenu,
    theme: theme,
    sideNavCollapsed: sideNavCollapsed,
    navType: navType,
    direction: direction,
    mobileNav: mobileNavCollapsed,
    userList: userList,
    custProgramList: custProgramList,
    masterData: masterData,
    masterOptions: masterOptions,
    gradeForTitle: gradeForTitle,
    reloadMasterData: reloadMasterData,
    changeToken: changeToken,
    changeUserDetails: changeUserDetails,
    changePermission: changePermission,
    changeSideMenu: changeSideMenu,
    changeSideNavState: changeSideNavState,
    changeNavType: changeNavType,
    changeTheme: changeTheme,
    changeDirection: changeDirection,
    changeMobileNavState: changeMobileNavState,
    changeUserList: changeUserList,
    changeCustProgramList: changeCustProgramList,
  };

  /**
   * Updates the authentication token.
   *
   * @param {string|null} e - The new token value.
   */
  function changeToken(e) {
    setValue(e);
  }

  function changeUserDetails(e) {
    setUser(e)
  }

  function changePermission(e) {
    setPermission(e);
  }

  function changeSideMenu(e) {
    setSideMenu(e);
  }

  /**
   * Toggles the side navigation collapsed state.
   */
  function changeSideNavState() {
    setSideNavCollapsed((prev) => !prev);
  }

  /**
   * Updates the navigation type and saves it to localStorage.
   *
   * @param {string} e - The new navigation type.
   */
  function changeNavType(e) {
    setNavType(e);
    localStorage.setItem('navtype', e);
  }

  /**
   * Updates the theme type and saves it to localStorage.
   *
   * @param {string} e - The new theme type.
   */
  function changeTheme(e) {
    setTheme(e);
    localStorage.setItem('theme', e);
  }

  /**
   * Updates the text direction.
   *
   * @param {string} e - The new text direction (LTR or RTL).
   */
  function changeDirection(e) {
    setDirection(e);
  }

  /**
   * Toggles the mobile navigation collapsed state.
   */
  function changeMobileNavState() {
    setMobileNavCollapsed((prev) => !prev);
  }

  function changeUserList(e) {
    setUserList(e)
  }
  
  function changeCustProgramList(e) {
    setCustProgramList(e)
  }
  

  return <RootContext.Provider value={ctx}>{children}</RootContext.Provider>;
}
